import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import { changeDateFormat } from '../../shared-functions/DateTime';

interface CustomDateRangeProperties {
  inputLabel?: string;
  value: string;
  name: string;
  placeholder?: string;
  onChangeValue: (name: string, value: string) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  error?: boolean;
  errorMessage?: string;
  minDate?: string;
  maxDate?: string;
  disablePastDates?: boolean;
  requiredTime?: boolean;
  timeFormat?: '24hr' | '12hr';
}

/**
 * Custom Date Picker component
 * @param {CustomDateRangeProperties} properties - Component properties
 * @returns The rendered CustomDatePicker component
 */
function CustomDatePicker(properties: CustomDateRangeProperties) {
  const {
    inputLabel,
    name,
    value,
    placeholder,
    onChangeValue,
    valueRequired,
    readOnly,
    fontSize,
    error,
    errorMessage,
    minDate,
    maxDate,
    disablePastDates,
    requiredTime,
    timeFormat,
  } = properties;

  const reference = useRef<HTMLDivElement>(null);
  const inputContainerReference = useRef<HTMLDivElement>(null);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [allDates, setAllDates] = useState<any[]>([]);
  const [style, setStyle] = useState<any>({});

  const [open, setOpen] = useState<boolean>(false);

  // Time picker local states
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedHours, setSelectedHours] = useState<number>(0);
  const [selectedMinutes, setSelectedMinutes] = useState<number>(0);
  const [selectedSeconds, setSelectedSeconds] = useState<number>(0);

  /**
   * Recalculates and updates the calendar portal position relative to the input container
   */
  const updatePosition = useCallback(() => {
    if (inputContainerReference.current) {
      const rect = inputContainerReference.current.getBoundingClientRect();
      let pickerWidth = 310;
      if (requiredTime) {
        pickerWidth = timeFormat === '12hr' ? 650 : 570;
      }
      const pickerHeight = 350;
      const margin = 8;
      const { innerHeight: viewportHeight, innerWidth: viewportWidth } = window;

      if (rect.bottom < 0 || rect.top > viewportHeight) {
        setOpen(false);
      } else {
        let top = rect.bottom + 4;
        if (
          rect.bottom + pickerHeight + margin > viewportHeight &&
          rect.top > pickerHeight + margin
        ) {
          top = rect.top - pickerHeight - 4;
        }

        let {left} = rect;
        if (rect.left + pickerWidth + margin > viewportWidth) {
          left = viewportWidth - pickerWidth - margin;
        }

        setStyle({
          position: 'fixed',
          top: `${top}px`,
          left: `${Math.max(margin, left)}px`,
          zIndex: 13000,
        });
      }
    }
  }, [requiredTime, timeFormat]);

  // Sync with value whenever calendar opens or value changes
  useEffect(() => {
    if (value) {
      const parts = value.split(' ');
      const [datePart, rawTimePart] = parts;
      const timePart = rawTimePart || '00:00:00';
      setSelectedDate(datePart);

      const timeSegments = timePart.split(':');
      const [hSegment, mSegment, sSegment] = timeSegments;
      setSelectedHours(parseInt(hSegment, 10) || 0);
      setSelectedMinutes(parseInt(mSegment, 10) || 0);
      setSelectedSeconds(parseInt(sSegment, 10) || 0);

      // Centering month and year to match chosen value date
      const dateObject = new Date(datePart);
      if (!Number.isNaN(dateObject.getTime())) {
        setCurrentMonth(dateObject.getMonth());
        setCurrentYear(dateObject.getFullYear());
      }
    } else {
      setSelectedDate('');
      setSelectedHours(0);
      setSelectedMinutes(0);
      setSelectedSeconds(0);
    }
  }, [value, open]);

  // Recalculate position on scroll and resize when calendar is open
  useEffect(() => {
    if (!open) {
      return () => {};
    }

    updatePosition();

    /**
     * Handles window scroll and resize events to keep calendar attached to input container
     */
    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open, updatePosition]);

  /**
   * Handle the Value change
   * @param {string} changeValue - The changeValue
   */
  const handleValueChange = (changeValue: string) => {
    let finalValue = changeValue;

    if (requiredTime && !changeValue.includes(':')) {
      finalValue = `${changeValue} 00:00:00`;
    }
    onChangeValue(name, finalValue);
    setOpen(false);
  };

  /**
   * Handle selected date click on calendar
   * @param {string} changeValue - The changeValue
   */
  const handleDateClick = (changeValue: string) => {
    if (requiredTime) {
      setSelectedDate(changeValue);
      const todayStrings = changeDateFormat(new Date().toString());
      if (changeValue === todayStrings) {
        const now = new Date();
        setSelectedHours(now.getHours());
        setSelectedMinutes(now.getMinutes());
        setSelectedSeconds(now.getSeconds());
      } else {
        setSelectedHours(0);
        setSelectedMinutes(0);
        setSelectedSeconds(0);
      }
    } else {
      handleValueChange(changeValue);
    }
  };

  /**
   * Apply full datetime combination
   */
  const handleApplyDateTime = () => {
    const dString = selectedDate || changeDateFormat(new Date().toString());
    const hourString = String(selectedHours).padStart(2, '0');
    const minuteString = String(selectedMinutes).padStart(2, '0');
    const secondString = String(selectedSeconds).padStart(2, '0');
    const finalValue = `${dString} ${hourString}:${minuteString}:${secondString}`;
    onChangeValue(name, finalValue);
    setOpen(false);
  };

  const is12HourFormat = timeFormat === '12hr';
  const selectedMeridiem: 'AM' | 'PM' = selectedHours < 12 ? 'AM' : 'PM';
  const selectedHour12 = selectedHours % 12 || 12;

  const todayString = changeDateFormat(new Date().toString());
  const isTodaySelected = selectedDate === todayString;

  /**
   * Check if hour option is disabled
   * @param {number} hour - Hour
   * @returns {boolean} Whether disabled
   */
  const isHourDisabled = (hour: number): boolean => {
    if (!isTodaySelected) return false;
    const now = new Date();
    const actual24Hr = is12HourFormat
      ? (hour % 12) + (selectedMeridiem === 'PM' ? 12 : 0)
      : hour;
    return actual24Hr < now.getHours();
  };

  /**
   * Check if minute option is disabled
   * @param {number} minute - Minute
   * @returns {boolean} Whether disabled
   */
  const isMinuteDisabled = (minute: number): boolean => {
    if (!isTodaySelected) return false;
    const now = new Date();
    if (selectedHours < now.getHours()) return true;
    if (selectedHours === now.getHours()) {
      return minute < now.getMinutes();
    }
    return false;
  };

  /**
   * Check if second option is disabled
   * @param {number} second - Second
   * @returns {boolean} Whether disabled
   */
  const isSecondDisabled = (second: number): boolean => {
    if (!isTodaySelected) return false;
    const now = new Date();
    if (selectedHours < now.getHours()) return true;
    if (selectedHours === now.getHours() && selectedMinutes < now.getMinutes()) return true;
    if (selectedHours === now.getHours() && selectedMinutes === now.getMinutes()) {
      return second < now.getSeconds();
    }
    return false;
  };

  /**
   * Handle hour selection from the hour column, in either 24hr or 12hr mode
   * @param {number} hour - The clicked hour value (0-23 in 24hr mode, 1-12 in 12hr mode)
   */
  const handleHourSelect = (hour: number) => {
    if (!is12HourFormat) {
      setSelectedHours(hour);
      return;
    }
    const normalizedHour = hour % 12;
    setSelectedHours(normalizedHour + (selectedMeridiem === 'PM' ? 12 : 0));
  };

  /**
   * Handle AM/PM toggle in 12hr mode, preserving the selected hour-of-12
   * @param {'AM' | 'PM'} meridiem - The chosen meridiem
   */
  const handleMeridiemSelect = (meridiem: 'AM' | 'PM') => {
    setSelectedHours((selectedHour12 % 12) + (meridiem === 'PM' ? 12 : 0));
  };

  /**
   * Format the stored (always 24hr) value string for display, converting to
   * 12hr AM/PM when timeFormat is '12hr'. Storage/submit value stays 24hr.
   * @param {string} rawValue - The stored "YYYY-MM-DD HH:mm:ss" value
   * @returns {string} the display string
   */
  const formatDisplayValue = (rawValue: string): string => {
    if (!requiredTime) return rawValue.split(' ')[0];
    if (!is12HourFormat) return rawValue;

    const [datePart, timePart] = rawValue.split(' ');
    if (!timePart) return rawValue;

    const [hourPart, minutePart, secondPart] = timePart.split(':');
    const hourNumber = parseInt(hourPart, 10) || 0;
    const meridiem = hourNumber < 12 ? 'AM' : 'PM';
    const hour12 = hourNumber % 12 || 12;

    return `${datePart} ${String(hour12).padStart(2, '0')}:${minutePart}:${secondPart} ${meridiem}`;
  };

  /**
   * Handle the Input click
   */
  const openCalenderView = () => {
    if (!readOnly) {
      setOpen(true);
      updatePosition();
    }
  };

  /**
   * Get the all days use month function
   * @param {number} month - The month
   * @param {number} year - The year
   */
  const getAllDays = useCallback(
    (month: number, year: number) => {
      const date = new Date(year, month, 1);
      let days = [];
      while (date.getMonth() === month) {
        days.push(new Date(date));
        date.setDate(date.getDate() + 1);
      }
      days = days.map((valueChange) => ({
        date: changeDateFormat(valueChange.toString()),
        number: valueChange.getDate(),
        day: valueChange.getDay(),
      }));
      if (days[0].day > 0) {
        const firstMonth = days[0].day;
        for (let index = 0; index < firstMonth; index += 1) {
          days = [{}, ...days];
        }
      }
      setAllDates(days);
    },
    [minDate, maxDate, disablePastDates],
  );

  /**
   * Check if previous month navigation should be disabled
   * @returns {boolean} Whether previous month navigation is disabled
   */
  const isPreviousMonthDisabled = (): boolean => {
    const viewDate = new Date(currentYear, currentMonth, 1);

    if (disablePastDates) {
      const today = new Date();
      const startOfTodayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      if (viewDate <= startOfTodayMonth) return true;
    }

    if (minDate) {
      const minDateObject = new Date(minDate);
      const startOfMinMonth = new Date(minDateObject.getFullYear(), minDateObject.getMonth(), 1);
      if (viewDate <= startOfMinMonth) return true;
    }

    return false;
  };

  /**
   * Check if next month navigation should be disabled
   * @returns {boolean} Whether next month navigation is disabled
   */
  const isNextMonthDisabled = (): boolean => {
    if (!maxDate) return false;

    const viewDate = new Date(currentYear, currentMonth, 1);
    const maxDateObject = new Date(maxDate);
    const startOfMaxMonth = new Date(maxDateObject.getFullYear(), maxDateObject.getMonth(), 1);

    if (viewDate >= startOfMaxMonth) return true;

    return false;
  };

  /**
   * Handle the month change function
   * @param {string} type - The type
   */
  const changeMonth = (type: string) => {
    let changeValue = 0;
    let changeYear = currentYear;
    if (type === 'add') {
      changeValue = currentMonth + 1;
      if (changeValue === months.length) {
        changeValue = 0;
        changeYear = currentYear + 1;
        setCurrentYear(currentYear + 1);
      }
      getAllDays(changeValue, changeYear);

      setCurrentMonth(changeValue);
    } else {
      changeValue = currentMonth - 1;
      if (changeValue === -1) {
        changeValue = 11;
        changeYear = currentYear - 1;
        setCurrentYear(currentYear - 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    }
  };

  /**
   * Handle close the calender overflow click function
   * @param {MouseEvent} event - The event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (
      reference.current &&
      !reference.current.contains(event.target as Node) &&
      inputContainerReference.current &&
      !inputContainerReference.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    getAllDays(currentMonth, currentYear);
  }, [currentMonth, currentYear, getAllDays]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`custom-input-date-wrap ${fontSize} ${error ? 'error' : ''}`}>
      {inputLabel && (
        <p className={`input-label ${error ? 'error' : ''}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-date-input">
        <div
          ref={inputContainerReference}
          className={`custom-input-container ${error ? 'error-border' : ''}`}
          onClick={openCalenderView}
          role="presentation">
          <div className="value-view">
            {value ? (
              <p className="selected-value">{formatDisplayValue(value)}</p>
            ) : (
              <p>{placeholder || (requiredTime ? 'DD/MM/YYYY 00:00:00' : 'DD/MM/YYYY')}</p>
            )}
          </div>

          <span className="picker-icon">
            <Calender />
          </span>
        </div>
        {open &&
          ReactDOM.createPortal(
            <div
              className={`calender-box ${requiredTime ? 'with-time' : ''} ${requiredTime && is12HourFormat ? 'with-meridiem' : ''}`}
              ref={reference}
              style={style}>
              <div className="calender-picker-layout">
                <div className="calender-date-side">
                  <div className="calender-header">
                    <div
                      className={`calender-header-left ${isPreviousMonthDisabled() ? 'disabled' : ''}`}
                      onClick={() => !isPreviousMonthDisabled() && changeMonth('sub')}
                      role="presentation">
                      <LeftArrow />
                    </div>
                    <div className="calender-header-center">
                      {months[currentMonth]} - {currentYear}
                    </div>
                    <div
                      className={`calender-header-right ${isNextMonthDisabled() ? 'disabled' : ''}`}
                      onClick={() => !isNextMonthDisabled() && changeMonth('add')}
                      role="presentation">
                      <RightArrow />
                    </div>
                  </div>
                  <div className="calender-body">
                    <div className="calender-header-items">
                      <div className="header-item">SUN</div>
                      <div className="header-item">MON</div>
                      <div className="header-item">TUS</div>
                      <div className="header-item">WEN</div>
                      <div className="header-item">THU</div>
                      <div className="header-item">FRI</div>
                      <div className="header-item">SAT</div>
                    </div>
                    <div className="calender-body-items">
                      {(() => {
                        const today = changeDateFormat(new Date().toString());
                        return allDates.map((dateDetail: any, dateIndex: number) => {
                          const isDisabled =
                            Object.keys(dateDetail).length !== 0 &&
                            ((disablePastDates && dateDetail.date < today) ||
                              (minDate && dateDetail.date < minDate) ||
                              (maxDate && dateDetail.date > maxDate));

                          const isActive =
                            Object.keys(dateDetail).length !== 0 &&
                            dateDetail.date === selectedDate;

                          return Object.keys(dateDetail).length === 0 ? (
                            <div key={`empty-${dateIndex}`} className="header-item" />
                          ) : (
                            <div
                              key={dateDetail.date}
                              className={`header-item ${isDisabled ? 'disabled' : ''} ${isActive ? 'active' : ''}`}
                              onClick={() => !isDisabled && handleDateClick(dateDetail.date)}
                              role="presentation">
                              {dateDetail.number}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>

                {requiredTime && (
                  <div className="calender-time-side">
                    <p className="time-side-title">Select Time</p>
                    <div className="time-picker-columns">
                      <div className="time-column hours-col">
                        <span className="column-label">HH</span>
                        <div className="column-options">
                          {Array.from({ length: is12HourFormat ? 12 : 24 }).map((_, index) => {
                            const hour = is12HourFormat ? index + 1 : index;
                            const hourString = String(hour).padStart(2, '0');
                            const isSelected = is12HourFormat
                              ? selectedHour12 === hour
                              : selectedHours === hour;
                            const disabled = isHourDisabled(hour);
                            return (
                              <button
                                key={hour}
                                type="button"
                                className={`time-option ${isSelected ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                                disabled={disabled}
                                onClick={() => !disabled && handleHourSelect(hour)}>
                                {hourString}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="time-column minutes-col">
                        <span className="column-label">MM</span>
                        <div className="column-options">
                          {Array.from({ length: 60 }).map((_, minute) => {
                            const minuteString = String(minute).padStart(2, '0');
                            const isSelected = selectedMinutes === minute;
                            const disabled = isMinuteDisabled(minute);
                            return (
                              <button
                                key={minute}
                                type="button"
                                className={`time-option ${isSelected ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                                disabled={disabled}
                                onClick={() => !disabled && setSelectedMinutes(minute)}>
                                {minuteString}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="time-column seconds-col">
                        <span className="column-label">SS</span>
                        <div className="column-options">
                          {Array.from({ length: 60 }).map((_, second) => {
                            const secondString = String(second).padStart(2, '0');
                            const isSelected = selectedSeconds === second;
                            const disabled = isSecondDisabled(second);
                            return (
                              <button
                                key={second}
                                type="button"
                                className={`time-option ${isSelected ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                                disabled={disabled}
                                onClick={() => !disabled && setSelectedSeconds(second)}>
                                {secondString}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {is12HourFormat && (
                        <div className="time-column meridiem-col">
                          <span className="column-label">AM/PM</span>
                          <div className="column-options">
                            {(['AM', 'PM'] as const).map((meridiem) => (
                              <button
                                key={meridiem}
                                type="button"
                                className={`time-option ${selectedMeridiem === meridiem ? 'active' : ''}`}
                                onClick={() => handleMeridiemSelect(meridiem)}>
                                {meridiem}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="time-picker-actions">
                      <button type="button" className="btn-cancel" onClick={() => setOpen(false)}>
                        Cancel
                      </button>
                      <button type="button" className="btn-apply" onClick={handleApplyDateTime}>
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )}
      </div>
      {error && errorMessage && <p className="error-message">{errorMessage}</p>}
    </div>
  );
}

CustomDatePicker.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  readOnly: false,
  fontSize: 'large',
  error: false,
  errorMessage: '',
  minDate: '',
  maxDate: '',
  disablePastDates: false,
  requiredTime: false,
  timeFormat: '24hr',
};

export default CustomDatePicker;
